import { Button, Card, Form, Input, Space, Statistic, Table, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import { formatMoney } from '@/utils/format';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type OutstandingRow = {
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  period_key: string;
  demand_count: number;
  outstanding_amount: number;
  uninvoiced_count: number;
  invoiced_unpaid_count: number;
};

type OutstandingPayload = {
  rows: OutstandingRow[];
  summary: {
    student_count: number;
    outstanding_total: number;
    period_key: string | null;
  };
};

export default function FeeOutstandingPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canDemand = Boolean(
    user?.permissions?.includes('education.fee.demand') || user?.permissions?.includes('education.admin')
  );

  const [periodKey, setPeriodKey] = useState('2026-Q1');
  const [q, setQ] = useState('');

  const outstanding = useQuery({
    queryKey: ['education-fee-outstanding', periodKey, q],
    queryFn: () =>
      api.get<OutstandingPayload>('/education/fees/outstanding', {
        params: {
          period_key: periodKey || undefined,
          q: q || undefined,
        },
      }),
  });

  const data = outstanding.data?.data;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['education-fee-outstanding'] });

  const bulkInvoice = useMutation({
    mutationFn: () =>
      api.post('/education/fees/demands/invoice-bulk', {
        period_key: periodKey || undefined,
        limit: 25,
      }),
    onSuccess: (result) => {
      const body = result.data as { succeeded?: number; failed?: number; attempted?: number };
      message.success(
        t('feeBulkInvoiceDone', {
          succeeded: body?.succeeded ?? 0,
          failed: body?.failed ?? 0,
          attempted: body?.attempted ?? 0,
        })
      );
      refresh();
      queryClient.invalidateQueries({ queryKey: ['education-fee-demands'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('feeOutstanding')}
      description={t('feeOutstandingIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/fees/demands')}>{t('feeDemands')}</Button>
          <Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => outstanding.refetch()}>
            <Form.Item label={t('periodKey')}>
              <Input
                value={periodKey}
                onChange={(e) => setPeriodKey(e.target.value)}
                placeholder="2026-Q1"
                style={{ width: 140 }}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('feeOutstandingSearchHint')}
                allowClear
                style={{ width: 220 }}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={outstanding.isFetching}>
              {t('filter')}
            </Button>
            {canDemand ? (
              <Button loading={bulkInvoice.isPending} onClick={() => bulkInvoice.mutate()}>
                {t('bulkCreateInvoices')}
              </Button>
            ) : null}
          </Form>
        </Card>

        <Card size="small" loading={outstanding.isLoading}>
          <Space size="large" wrap>
            <Statistic title={t('hubStatStudents')} value={data?.summary?.student_count ?? 0} />
            <Statistic
              title={t('outstanding')}
              value={data?.summary?.outstanding_total ?? 0}
              precision={2}
            />
          </Space>
        </Card>

        <Card size="small">
          <Table
            rowKey={(row) => `${row.student_id}-${row.period_key}`}
            size="small"
            loading={outstanding.isLoading}
            dataSource={data?.rows ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: OutstandingRow) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              { title: t('periodKey'), dataIndex: 'period_key', width: 110 },
              { title: t('demandCount'), dataIndex: 'demand_count', width: 100 },
              {
                title: t('outstanding'),
                dataIndex: 'outstanding_amount',
                width: 120,
                render: (v: number) =>
                  formatMoney(Number(v), { currency: 'INR', display: 'plain', locale: 'en-IN' }),
              },
              {
                title: t('uninvoiced'),
                dataIndex: 'uninvoiced_count',
                width: 100,
              },
              {
                title: t('invoicedUnpaid'),
                dataIndex: 'invoiced_unpaid_count',
                width: 120,
              },
              {
                title: t('actions'),
                width: 120,
                render: (_: unknown, row: OutstandingRow) => (
                  <Button size="small" onClick={() => navigate(`/education/fees/students/${row.student_id}`)}>
                    {t('ledger')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
